import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Alert,
    CircularProgress,
    Grid,
    Checkbox,
    ListItemText,
    OutlinedInput,
    SelectChangeEvent,
} from '@mui/material';
import { useState, useEffect } from 'react';
import {
    paymentsApi,
    CaProduct,
    CaTaxCategory,
    CaEventType,
    CreateProductRequest,
    CaProductsResult,
    CaPaymentMethod,
} from '../services/payments-api.service';
import { ApiError } from '../services/base-api.service';

interface ProductFormDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    product?: CaProduct | null;
    mode: 'create' | 'edit';
}

interface FormData {
    productSku: string;
    taxCategory: string;
    eventType: string;
    paymentMethods: CaPaymentMethod[];
    numberOfPayments: string;
    adminFee: string;
    nameEn: string;
    nameFr: string;
}

const initialFormData: FormData = {
    productSku: '',
    taxCategory: '',
    eventType: '',
    paymentMethods: [],
    numberOfPayments: '1',
    adminFee: '0',
    nameEn: '',
    nameFr: '',
};

const paymentMethodOptions = [
    { value: CaPaymentMethod.creditCard, label: 'Credit Card' },
    { value: CaPaymentMethod.eTransfer, label: 'eTransfer' },
    { value: CaPaymentMethod.account, label: 'Account' },
];

export function ProductFormDialog({ open, onClose, onSave, product, mode }: ProductFormDialogProps) {
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [taxCategories, setTaxCategories] = useState<CaTaxCategory[]>([]);
    const [eventTypes, setEventTypes] = useState<CaEventType[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Load dropdown data and set form data when modal opens
    useEffect(() => {
        if (open) {
            loadDropdownData();
            if (mode === 'edit' && product) {
                setFormData({
                    productSku: product.productSku,
                    taxCategory: product.taxCategory === 'undefined' ? '' : product.taxCategory,
                    eventType: product.eventType === 'undefined' ? '' : product.eventType,
                    paymentMethods: product.paymentMethods || [],
                    numberOfPayments: product.numberOfPayments?.toString() || '1',
                    adminFee: product.adminFee?.toString() || '0',
                    nameEn: product.translations?.en?.name || '',
                    nameFr: product.translations?.fr?.name || '',
                });
            } else {
                setFormData(initialFormData);
            }
            setError(null);
            setFormErrors({});
        }
    }, [open, mode, product]);

    const loadDropdownData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [taxCategoriesRes, eventTypesRes] = await Promise.all([
                paymentsApi.getTaxCategories(),
                paymentsApi.getEventTypes(),
            ]);

            // Convert string arrays to objects with code and name
            const taxCategoriesObjects = taxCategoriesRes.map((code) => ({
                code,
                name: code,
            }));

            const eventTypesObjects = eventTypesRes.map((code) => ({
                code,
                name: code,
            }));

            setTaxCategories(taxCategoriesObjects);
            setEventTypes(eventTypesObjects);
        } catch (err) {
            if (err && typeof err === 'object' && 'message' in err) {
                // Handle ApiError with specific server message
                setError((err as ApiError).message);
            } else if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Failed to load dropdown data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // Clear field error when user starts typing
        if (formErrors[field]) {
            setFormErrors((prev) => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const handlePaymentMethodsChange = (event: SelectChangeEvent<CaPaymentMethod[]>) => {
        const value = event.target.value;
        setFormData((prev) => ({
            ...prev,
            paymentMethods: typeof value === 'string' ? (value.split(',') as CaPaymentMethod[]) : value,
        }));
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.productSku.trim()) {
            errors.productSku = 'Product code is required';
        }

        if (!formData.taxCategory) {
            errors.taxCategory = 'Tax category is required';
        }

        if (!formData.eventType) {
            errors.eventType = 'MFI Event type is required';
        }

        if (!formData.nameEn.trim()) {
            errors.nameEn = 'English name is required';
        }

        if (!formData.nameFr.trim()) {
            errors.nameFr = 'French name is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);
            setError(null);

            const request: CreateProductRequest = {
                productSku: formData.productSku.trim(),
                taxCategory: formData.taxCategory,
                eventType: formData.eventType,
                paymentMethods: formData.paymentMethods,
                numberOfPayments: parseInt(formData.numberOfPayments, 10) || 1,
                adminFee: parseInt(formData.adminFee, 10) || 0,
                translations: {
                    en: {
                        name: formData.nameEn.trim(),
                        ...(mode === 'edit' && product?.translations?.en?.id && { id: product.translations.en.id }),
                    },
                    fr: {
                        name: formData.nameFr.trim(),
                        ...(mode === 'edit' && product?.translations?.fr?.id && { id: product.translations.fr.id }),
                    },
                },
            };

            let response: CaProductsResult;
            if (mode === 'create') {
                response = await paymentsApi.createProduct(request);
            } else {
                response = await paymentsApi.updateProduct(request);
            }

            // Check for errors in the response
            if (response.errors && response.errors.length > 0) {
                throw new Error(response.errors.join(', '));
            }

            onSave();
            onClose();
        } catch (err) {
            if (err && typeof err === 'object' && 'message' in err) {
                // Handle ApiError with specific server message
                setError((err as ApiError).message);
            } else if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Failed to save product');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            onClose();
        }
    };

    const title = mode === 'create' ? 'Create Product' : 'Edit Product';
    const saveButtonText = mode === 'create' ? 'Create' : 'Update';

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && (
                    <Box sx={{ pt: 1 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Product SKU"
                                    value={formData.productSku}
                                    onChange={(e) => handleInputChange('productSku', e.target.value)}
                                    error={!!formErrors.productSku}
                                    helperText={formErrors.productSku}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth error={!!formErrors.eventType}>
                                    <InputLabel>MFI Event Type</InputLabel>
                                    <Select
                                        value={formData.eventType}
                                        onChange={(e) => handleInputChange('eventType', e.target.value)}
                                        label="Event Type"
                                    >
                                        {eventTypes.map((type) => (
                                            <MenuItem key={type.code} value={type.code}>
                                                {type.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formErrors.eventType && (
                                        <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                                            {formErrors.eventType}
                                        </Box>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth error={!!formErrors.taxCategory}>
                                    <InputLabel>Tax Category</InputLabel>
                                    <Select
                                        value={formData.taxCategory}
                                        onChange={(e) => handleInputChange('taxCategory', e.target.value)}
                                        label="Tax Category"
                                    >
                                        {taxCategories.map((category) => (
                                            <MenuItem key={category.code} value={category.code}>
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formErrors.taxCategory && (
                                        <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                                            {formErrors.taxCategory}
                                        </Box>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Payment Methods</InputLabel>
                                    <Select
                                        multiple
                                        value={formData.paymentMethods}
                                        onChange={handlePaymentMethodsChange}
                                        input={<OutlinedInput label="Payment Methods" />}
                                        renderValue={(selected) =>
                                            selected
                                                .map(
                                                    (val) =>
                                                        paymentMethodOptions.find((opt) => opt.value === val)?.label ||
                                                        val,
                                                )
                                                .join(', ')
                                        }
                                    >
                                        {paymentMethodOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Checkbox checked={formData.paymentMethods.includes(option.value)} />
                                                <ListItemText primary={option.label} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Number of Payments"
                                    type="number"
                                    value={formData.numberOfPayments}
                                    onChange={(e) => handleInputChange('numberOfPayments', e.target.value)}
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Admin Fee (cents)"
                                    type="number"
                                    value={formData.adminFee}
                                    onChange={(e) => handleInputChange('adminFee', e.target.value)}
                                    inputProps={{ min: 0 }}
                                    helperText="e.g., 1000 = $10.00"
                                />
                            </Grid>

                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Name (English)"
                                    value={formData.nameEn}
                                    onChange={(e) => handleInputChange('nameEn', e.target.value)}
                                    error={!!formErrors.nameEn}
                                    helperText={formErrors.nameEn}
                                />
                            </Grid>

                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Nom (Français)"
                                    value={formData.nameFr}
                                    onChange={(e) => handleInputChange('nameFr', e.target.value)}
                                    error={!!formErrors.nameFr}
                                    helperText={formErrors.nameFr}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || saving}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                    {saving ? 'Saving...' : saveButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
